import dateFunctions, { isDateEarlierThan, isDateValid } from "../../../modules/definition/calendar/dateFunctions";
import dateByAddingSpan from "../../../modules/definition/calendar/dateFunctions/dateByAddingSpan";
import { stringFromDate, stringFromEndDate } from "../../../modules/definition/calendar/dateFunctions/stringFromDate";
import itemUtils from "../../../modules/items/utils";
import { immutableRecords } from "../../../types";
import { DateDisplayRecord } from "../../../types/immutableRecords/display/shared/DateDisplayRecord";
import { stringFromTimespan } from "../../definition/calendar/dateFunctions/timespanFunctions";

/**
 * @param {DateRecord} date
 * @param {CalendarRecord} calendar
 * @param {boolean} isEndDate
 * @param {DateRecord} startDate
 * @return {DateDisplayRecord}
 */
const convertToDisplayDate = (date, calendar, isEndDate, startDate) => {
  if (isDateValid(date)) {
    const label = isEndDate ? stringFromEndDate(calendar, date, startDate) : stringFromDate(calendar, date);
    return immutableRecords.DateDisplayRecord(date).set("label", label);
  }
  return null;
};

/**
 *
 * @param {DateRecord} start
 * @param {TimespanRecord} duration
 * @param {CalendarRecord} calendar
 */
const getDatesValues = (start, duration, calendar) => {
  let returnObj = {};
  if (isDateValid(start)) {
    const startDate = convertToDisplayDate(start, calendar);
    returnObj["startDate"] = startDate;
    if (duration) {
      const endDate = convertToDisplayDate(dateByAddingSpan(calendar, start, duration), calendar, true, start);
      returnObj["endDate"] = endDate;
    }
  }
  if (duration) {
    const durationLabel = stringFromTimespan(duration);
    returnObj["duration"] = immutableRecords.TimespanDisplayRecord(duration).set("label", durationLabel);
  }
  return returnObj;
};

/**
 * If end date is later than today, keep the end date
 * If start date is later than today, keep the end date and set label to empty
 * @param {DateRecord|DateDisplayRecord} startDate
 * @param {DateRecord|DateDisplayRecord} endDate
 * @param {CalendarRecord} calendar
 * @param {DateDisplayRecord} displayTodayDate
 */
const adjustOngoingEndDate = (startDate, endDate, calendar, displayTodayDate, hasDescendantBeyondNow) => {
  if (!hasDescendantBeyondNow) {
    return displayTodayDate;
  }
  return isDateValid(startDate)
    ? isDateValid(endDate) && isDateEarlierThan(calendar, displayTodayDate, endDate)
      ? isDateEarlierThan(calendar, displayTodayDate, startDate)
        ? displayTodayDate
        : endDate
      : displayTodayDate
    : endDate;
};

/**
 *
 * @param {DateRecord} inStartDate
 * @param {TimespanRecord} inDuration
 * @param {DateRecord} inPreferredStartDate
 * @param {TimespanRecord} inPreferredDuration
 * @param {DateRecord} inLatestStartDate
 * @param {TimespanRecord} inDurationToEarliestEnd
 * @param {bool} isOngoing
 * @param {bool} hasOngoingDescendants
 * @param {DateRecord} todayDate
 * @param {bool} hasDescendantBeyondNow
 * @param {CalendarRecord} calendar
 */
const getItemDisplayDates = (
  inStartDate,
  inDuration,
  inPreferredStartDate,
  inPreferredDuration,
  inLatestStartDate,
  inDurationToEarliestEnd,
  isOngoing,
  hasOngoingDescendants,
  todayDate,
  hasDescendantBeyondNow,
  calendar
) => {
  let finalObj = {
    latestStartDate: inLatestStartDate,
    startDate: inStartDate,
    duration: inDuration,
    preferredStartDate: inPreferredStartDate,
    preferredDuration: inPreferredDuration,
    durationToEarliestEnd: inDurationToEarliestEnd,
    hasOngoingDescendants: hasOngoingDescendants
  };

  const displayTodayDate = DateDisplayRecord(todayDate).set("label", "Present");

  // Get latest start date
  finalObj.latestStartDate = convertToDisplayDate(finalObj.latestStartDate, calendar);

  // Start, End, and Duration
  const dateValues = getDatesValues(finalObj.startDate, finalObj.duration, calendar);
  if (isDateValid(dateValues.startDate)) {
    finalObj.startDate = dateValues.startDate;
    // If end date matched lastStartDate, we need to use the latestStartDate as the end date
    finalObj.endDate =
      isDateValid(finalObj.latestStartDate) && dateFunctions.areDatesSame(finalObj.latestStartDate, dateValues.endDate)
        ? finalObj.latestStartDate
        : dateValues.endDate;
  }
  finalObj.duration = inDuration;

  // Preferred Dates
  const preferredDateValues = getDatesValues(finalObj.preferredStartDate, finalObj.preferredDuration, calendar);
  if (isDateValid(preferredDateValues.startDate)) {
    finalObj.preferredStartDate = preferredDateValues.startDate;
    // If end date matched lastStartDate, we need to use the latestStartDate as the end date
    finalObj.preferredEndDate =
      isDateValid(finalObj.latestStartDate) &&
      dateFunctions.areDatesSame(finalObj.latestStartDate, preferredDateValues.endDate)
        ? finalObj.latestStartDate
        : preferredDateValues.endDate;
  }

  // And work out what preferredEndDate we will draw...
  finalObj.drawPreferredEndDate = isDateValid(finalObj.preferredEndDate)
    ? finalObj.preferredEndDate
    : isDateValid(finalObj.startDate)
      ? dateByAddingSpan(calendar, finalObj.startDate, finalObj.preferredDuration)
      : immutableRecords.DateRecord();

  // Earliest Date Values
  const earliestDateValues = getDatesValues(finalObj.preferredStartDate, finalObj.durationToEarliestEnd, calendar);
  if (earliestDateValues.startDate) {
    finalObj.earliestEndDate =
      isDateValid(finalObj.latestStartDate) &&
      isDateValid(earliestDateValues.endDate) &&
      dateFunctions.areDatesSame(finalObj.latestStartDate, earliestDateValues.endDate)
        ? finalObj.latestStartDate
        : earliestDateValues.endDate;
  }
  finalObj.durationToEarliestEnd = earliestDateValues.duration;

  if (isOngoing) {
    finalObj.endDate = adjustOngoingEndDate(
      finalObj.startDate,
      finalObj.endDate,
      calendar,
      displayTodayDate,
      hasDescendantBeyondNow
    );

    finalObj.preferredEndDate = adjustOngoingEndDate(
      finalObj.preferredStartDate,
      finalObj.preferredEndDate,
      calendar,
      displayTodayDate,
      hasDescendantBeyondNow
    );

    finalObj.earliestEndDate = finalObj.earliestEndDate
      ? adjustOngoingEndDate(
        finalObj.preferredStartDate,
        finalObj.earliestEndDate,
        calendar,
        displayTodayDate,
        hasDescendantBeyondNow
      )
      : finalObj.earliestEndDate;

    const ongoingDuration = itemUtils.getOngoingDuration(
      finalObj.preferredStartDate,
      finalObj.latestStartDate,
      calendar,
      todayDate
    );

    finalObj.preferredDuration = ongoingDuration;
    // Only set duration to today if item has no children, or if its children end before today
    if (!hasDescendantBeyondNow) {
      finalObj.duration = ongoingDuration ? ongoingDuration : finalObj.duration.set("label", "");
    }
  } else {
    // Update the duration when uncertain dates available
    const durationLabel = itemUtils.getDurationRangeLabelForDates(
      finalObj.preferredStartDate,
      finalObj.latestStartDate,
      finalObj.preferredDuration,
      finalObj.durationToEarliestEnd,
      calendar
    );

    // If there are ongoing children, adjust the end date label to today and show "present"
    // If item has ongoing children, use the Present label if today is later than endDate
    if (hasOngoingDescendants) {
      if (isDateValid(finalObj.endDate) && isDateEarlierThan(calendar, finalObj.endDate, displayTodayDate)) {
        finalObj.endDate = displayTodayDate;
        finalObj.duration = itemUtils.getOngoingDuration(
          finalObj.startDate,
          finalObj.latestStartDate,
          calendar,
          todayDate
        );
      }
    } else {
      finalObj.duration = dateValues.duration.set("label", durationLabel);
    }
    finalObj.preferredDuration = preferredDateValues.duration.set("label", durationLabel);
  }
  return immutableRecords.ItemDatesRecord(finalObj);
};

export default getItemDisplayDates;
